import React, { useCallback, useEffect } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Icon,
  SimpleGrid,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaEdit, FaTrash } from 'react-icons/fa';
import PageLoader from 'components/wholePageLoader/PageLoader';
import BackButton from 'components/backButton/BackButton';
import CommonTable from 'components/commonTable/CommonTable';
import ConfirmModal from 'components/popUpModel/DeleteModal';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import { sideBarRoutes } from 'utils/StaticData';
import {
  DeletePayoutSetting,
  GetPayoutSettingById,
} from 'reactRedux/thunks/payoutSettingThunk/payoutSettingThunk';
import moment from 'moment';

const DAY_NAMES = [
  '',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const PayoutSettingDetail = () => {
  const { payoutSettingDetailId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { tabletextColor } = useColorModeVariables();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { adminDetails } = useSelector((state) => state.authSlice);
  const { isViewLoading, isLoading, viewPayoutSetting } = useSelector(
    (state) => state.PayoutSettingSlice,
  );

  const isAdmin = adminDetails?.role === sideBarRoutes?.Admin;

  const fetchDetail = useCallback(() => {
    dispatch(GetPayoutSettingById(payoutSettingDetailId));
  }, [dispatch, payoutSettingDetailId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const columnHelper = createColumnHelper();

  const dealerColumns = [
    columnHelper.accessor(
      (row) => `${row?.firstName || ''} ${row?.lastName || ''}`.trim(),
      {
        id: 'name',
        header: () => (
          <Text
            whiteSpace="nowrap"
            fontSize={{ sm: '10px', lg: '12px' }}
            color="gray.400"
            fontFamily="Montserrat"
          >
            Name
          </Text>
        ),
        cell: (info) => (
          <Text
            color={tabletextColor}
            fontSize="sm"
            fontWeight="600"
            whiteSpace="nowrap"
          >
            {info.getValue() || 'N/A'}
          </Text>
        ),
      },
    ),
    columnHelper.accessor((row) => row?.email, {
      id: 'email',
      header: () => (
        <Text
          whiteSpace="nowrap"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Email
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" whiteSpace="nowrap">
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
  ];

  const getScheduleLabel = () => {
    if (!viewPayoutSetting) return 'N/A';
    const {
      payoutFrequency,
      scheduledDayOfWeek,
      scheduledDayOfMonth,
      scheduledTime,
    } = viewPayoutSetting;
    if (payoutFrequency === 'weekly' && scheduledDayOfWeek && scheduledTime) {
      return `${DAY_NAMES[scheduledDayOfWeek] || scheduledDayOfWeek} at ${scheduledTime} UTC`;
    }
    if (payoutFrequency === 'monthly' && scheduledDayOfMonth && scheduledTime) {
      return `Day ${scheduledDayOfMonth} of each month at ${scheduledTime} UTC`;
    }
    return 'N/A';
  };

  const handleDelete = () => {
    dispatch(
      DeletePayoutSetting(payoutSettingDetailId, () => {
        onClose();
        navigate('/admin/payout-settings');
      }),
    );
  };

  if (isViewLoading) return <PageLoader />;

  return (
    <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
      <Flex justify="space-between" align="center" mb="20px">
        <BackButton
          returnUrl={location.state?.returnUrl || '/admin/payout-settings'}
        />
        {isAdmin && (
          <Flex gap="12px">
            <Button
              bg="reviveOrange"
              color="primary"
              borderRadius="12px"
              _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
              onClick={() =>
                navigate(`/admin/payout-settings/${payoutSettingDetailId}/edit`)
              }
            >
              <Icon
                as={FaEdit}
                width="15px"
                height="15px"
                marginRight="5px"
                color="inherit"
              />
              Edit Setting
            </Button>
            <Button
              bg="red.500"
              color="white"
              borderRadius="12px"
              _hover={{ bg: 'red.600' }}
              onClick={onOpen}
            >
              <Icon
                as={FaTrash}
                width="15px"
                height="15px"
                marginRight="5px"
                color="inherit"
              />
              Delete
            </Button>
          </Flex>
        )}
      </Flex>

      {viewPayoutSetting ? (
        <>
          {/* Main Details */}
          <Card
            w="100%"
            boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
            borderRadius="20px"
            paddingInline="40px"
            paddingBlock="40px"
            mt="20px"
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Flex
                alignItems={{ sm: 'start', md: 'center' }}
                direction={{ sm: 'column', md: 'row' }}
                gap="10px"
                padding="4px 8px"
                borderLeft="1px solid #ccc"
              >
                <Text fontWeight="normal">Platform Account:</Text>
                <Text fontWeight="semibold">
                  {viewPayoutSetting?.platformAccountName || 'N/A'}
                </Text>
              </Flex>
              <Flex
                alignItems={{ sm: 'start', md: 'center' }}
                direction={{ sm: 'column', md: 'row' }}
                gap="10px"
                padding="4px 8px"
                borderLeft="1px solid #ccc"
              >
                <Text fontWeight="normal">Connected Account:</Text>
                <Text fontWeight="semibold">
                  {viewPayoutSetting?.connectedAccountName || 'N/A'}
                </Text>
              </Flex>
              <Flex
                alignItems={{ sm: 'start', md: 'center' }}
                direction={{ sm: 'column', md: 'row' }}
                gap="10px"
                padding="4px 8px"
                borderLeft="1px solid #ccc"
              >
                <Text fontWeight="normal">Connected Account ID:</Text>
                <Text fontWeight="semibold">
                  {viewPayoutSetting?.connectedAccountId || 'N/A'}
                </Text>
              </Flex>
              <Flex
                alignItems={{ sm: 'start', md: 'center' }}
                direction={{ sm: 'column', md: 'row' }}
                gap="10px"
                padding="4px 8px"
                borderLeft="1px solid #ccc"
              >
                <Text fontWeight="normal">Country:</Text>
                <Text fontWeight="semibold">
                  {viewPayoutSetting?.connectedAccountCountry || 'N/A'}
                </Text>
              </Flex>
              <Flex
                alignItems={{ sm: 'start', md: 'center' }}
                direction={{ sm: 'column', md: 'row' }}
                gap="10px"
                padding="4px 8px"
                borderLeft="1px solid #ccc"
              >
                <Text fontWeight="normal">Split Ratio:</Text>
                <Text fontWeight="semibold">
                  {viewPayoutSetting?.revenueSplitRatio !== null &&
                  viewPayoutSetting?.revenueSplitRatio !== undefined
                    ? `${viewPayoutSetting.revenueSplitRatio}%`
                    : 'N/A'}
                </Text>
              </Flex>
              <Flex
                alignItems={{ sm: 'start', md: 'center' }}
                direction={{ sm: 'column', md: 'row' }}
                gap="10px"
                padding="4px 8px"
                borderLeft="1px solid #ccc"
              >
                <Text fontWeight="normal">Frequency:</Text>
                <Text fontWeight="semibold" textTransform="capitalize">
                  {viewPayoutSetting?.payoutFrequency || 'N/A'}
                </Text>
              </Flex>
              <Flex
                alignItems={{ sm: 'start', md: 'center' }}
                direction={{ sm: 'column', md: 'row' }}
                gap="10px"
                padding="4px 8px"
                borderLeft="1px solid #ccc"
              >
                <Text fontWeight="normal">Schedule:</Text>
                <Text fontWeight="semibold">{getScheduleLabel()}</Text>
              </Flex>
              <Flex
                alignItems={{ sm: 'start', md: 'center' }}
                direction={{ sm: 'column', md: 'row' }}
                gap="10px"
                padding="4px 8px"
                borderLeft="1px solid #ccc"
              >
                <Text fontWeight="normal">Status:</Text>
                <Badge
                  colorScheme={viewPayoutSetting?.isActive ? 'green' : 'gray'}
                  variant="solid"
                  fontSize="xs"
                >
                  {viewPayoutSetting?.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </Flex>
              {Array.isArray(viewPayoutSetting?.next3SettlementDates) &&
                viewPayoutSetting.next3SettlementDates.length > 0 && (
                  <Flex
                    alignItems={{ sm: 'start', md: 'start' }}
                    direction="column"
                    gap="4px"
                    padding="4px 8px"
                    borderLeft="1px solid #ccc"
                    gridColumn={{ md: 'span 2' }}
                  >
                    <Text fontWeight="normal">Next Settlements:</Text>
                    {viewPayoutSetting.next3SettlementDates.map((d, i) => (
                      <Text key={i} fontWeight="semibold" fontSize="sm">
                        {`${i + 1}. ${moment(d).utc().format('ddd, DD MMM YYYY HH:mm [UTC]')}`}
                      </Text>
                    ))}
                  </Flex>
                )}
            </SimpleGrid>
          </Card>

          {/* Associated Dealers */}
          <Box pt="30px" alignItems="left">
            <Text fontSize="2xl" fontWeight="medium">
              Associated Dealers
            </Text>
          </Box>
          <Card
            w="100%"
            boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
            borderRadius="20px"
            paddingInline="40px"
            paddingBlock="40px"
            overflowX={{ sm: 'scroll', lg: 'hidden' }}
            mt="20px"
          >
            {Array.isArray(viewPayoutSetting?.associatedDealers) &&
            viewPayoutSetting.associatedDealers.length > 0 ? (
              <CommonTable
                columns={dealerColumns}
                data={viewPayoutSetting.associatedDealers}
                isLoading={false}
                keyValue="id"
                rowclick={[]}
                isLink={false}
              />
            ) : (
              <Text color="gray.400">
                No dealers associated with this Stripe account.
              </Text>
            )}
          </Card>
        </>
      ) : (
        <Text>No payout setting found.</Text>
      )}

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        title="Are you sure you want to delete this payout setting? This action cannot be undone."
        SolidBTNText="Delete"
        onDelete={handleDelete}
        loader={isLoading}
      />
    </Box>
  );
};

export default PayoutSettingDetail;
